import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getApplicationUser, updateApplicationUserDisplayCurrency } from 'src/api/adminApi';
import {
  defaultDisplayCurrency,
  DisplayCurrency,
  normalizeDisplayCurrency
} from 'src/utils/currencyFormatting';

interface DisplayCurrencyContextType {
  displayCurrency: DisplayCurrency;
  isLoading: boolean;
  isReady: boolean;
  isSaving: boolean;
  updateDisplayCurrency: (displayCurrency: DisplayCurrency) => Promise<void>;
}

const DisplayCurrencyContext = createContext<DisplayCurrencyContextType>({
  displayCurrency: defaultDisplayCurrency,
  isLoading: true,
  isReady: false,
  isSaving: false,
  updateDisplayCurrency: async () => {}
});

export const useDisplayCurrency = () => useContext(DisplayCurrencyContext);

export const DisplayCurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>(defaultDisplayCurrency);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadDisplayCurrency = async () => {
      if (isAuthLoading) {
        return;
      }

      if (!isAuthenticated) {
        setDisplayCurrency(defaultDisplayCurrency);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const applicationUser = await getApplicationUser();
        setDisplayCurrency(normalizeDisplayCurrency(applicationUser.displayCurrency));
      } catch (error) {
        console.error('Failed to load display currency preference:', error);
        setDisplayCurrency(defaultDisplayCurrency);
      } finally {
        setIsLoading(false);
      }
    };

    loadDisplayCurrency();
  }, [isAuthenticated, isAuthLoading]);

  const updateDisplayCurrency = async (nextDisplayCurrency: DisplayCurrency) => {
    const previousDisplayCurrency = displayCurrency;
    setDisplayCurrency(nextDisplayCurrency);
    setIsSaving(true);

    try {
      const applicationUser = await updateApplicationUserDisplayCurrency(nextDisplayCurrency);
      setDisplayCurrency(normalizeDisplayCurrency(applicationUser.displayCurrency));
    } catch (error) {
      console.error('Failed to update display currency preference:', error);
      setDisplayCurrency(previousDisplayCurrency);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DisplayCurrencyContext.Provider
      value={{
        displayCurrency,
        isLoading,
        isReady: !isLoading,
        isSaving,
        updateDisplayCurrency
      }}
    >
      {children}
    </DisplayCurrencyContext.Provider>
  );
};